/**
 * ObjectiveSystem - Manages mission objectives, tracking, and completion
 * Handles objective creation, progress tracking, and completion detection
 */

import { store } from '../../state/store';

/**
 * Objective types based on game design
 */
export enum ObjectiveType {
  // Survival objectives
  SURVIVE = 'survive', // Survive until specific time
  RETURN_TO_OFFICE = 'return_to_office', // Return to security office

  // Collection objectives
  COLLECT_ITEM = 'collect_item', // Collect specific item(s)
  FIND_COLLECTIBLE = 'find_collectible', // Find lore collectibles

  // Exploration objectives
  EXPLORE_AREA = 'explore_area', // Enter/explore specific area
  REACH_LOCATION = 'reach_location', // Reach a specific location

  // Interaction objectives
  INTERACT_WITH = 'interact_with', // Interact with object
  REPAIR_EQUIPMENT = 'repair_equipment', // Fix/repair something
  ACTIVATE_SWITCH = 'activate_switch', // Activate a switch/button

  // Puzzle objectives
  SOLVE_PUZZLE = 'solve_puzzle', // Complete a puzzle
  ENTER_CODE = 'enter_code', // Enter security code

  // Avoidance objectives
  AVOID_DETECTION = 'avoid_detection', // Avoid being spotted by robots
  MAINTAIN_RESOURCE = 'maintain_resource', // Keep battery/power above threshold
}

/**
 * Objective definition
 */
export interface ObjectiveDefinition {
  id: string;
  type: ObjectiveType;
  title: string; // Short title (e.g., "Survive until 6 AM")
  description: string; // Longer description for details
  required: boolean; // Required to complete night vs optional
  hidden: boolean; // Hidden until revealed by game event

  // Progress tracking
  targetValue?: number; // Target value (e.g., collect 3 items)
  currentValue?: number; // Current progress

  // Conditions
  requiredItems?: string[]; // Required item IDs from inventory
  requiredLocation?: string; // Required location/area ID
  requiredTime?: number; // Required time elapsed (seconds)

  // Metadata
  rewardXP?: number; // XP awarded on completion
  completedAt?: number; // Timestamp when completed
}

/**
 * Objective progress update
 */
export interface ObjectiveProgress {
  objectiveId: string;
  currentValue: number;
  completed: boolean;
}

/**
 * Objective System - Manages all objectives for each night
 */
export class ObjectiveSystem {
  private static instance: ObjectiveSystem;

  // All objectives for current night
  private objectives: Map<string, ObjectiveDefinition> = new Map();

  // Completed objective IDs
  private completedObjectives: Set<string> = new Set();

  // Callbacks
  private onObjectiveCompletedCallbacks: Array<(objective: ObjectiveDefinition) => void> = [];
  private onObjectiveRevealedCallbacks: Array<(objective: ObjectiveDefinition) => void> = [];
  private onObjectiveProgressCallbacks: Array<(objective: ObjectiveDefinition) => void> = [];
  private onAllObjectivesCompleteCallbacks: Array<() => void> = [];

  private constructor() {
    // Initialize with empty state
  }

  public static getInstance(): ObjectiveSystem {
    if (!ObjectiveSystem.instance) {
      ObjectiveSystem.instance = new ObjectiveSystem();
    }
    return ObjectiveSystem.instance;
  }

  /**
   * Add a new objective to the current night
   */
  public addObjective(objective: ObjectiveDefinition): void {
    // Set default values
    if (objective.currentValue === undefined) {
      objective.currentValue = 0;
    }

    this.objectives.set(objective.id, objective);
    console.log(`🎯 Added objective: ${objective.title}`);

    // If not hidden, trigger revealed callback
    if (!objective.hidden) {
      this.onObjectiveRevealedCallbacks.forEach(cb => cb(objective));
    }
  }

  /**
   * Reveal a hidden objective
   */
  public revealObjective(objectiveId: string): boolean {
    const objective = this.objectives.get(objectiveId);
    if (!objective) {
      console.warn(`Objective not found: ${objectiveId}`);
      return false;
    }

    if (!objective.hidden) {
      console.warn(`Objective already revealed: ${objectiveId}`);
      return false;
    }

    objective.hidden = false;
    console.log(`👁️ Revealed objective: ${objective.title}`);

    this.onObjectiveRevealedCallbacks.forEach(cb => cb(objective));
    return true;
  }

  /**
   * Update objective progress
   */
  public updateProgress(objectiveId: string, value: number, increment: boolean = false): boolean {
    const objective = this.objectives.get(objectiveId);
    if (!objective) {
      console.warn(`Objective not found: ${objectiveId}`);
      return false;
    }

    if (this.completedObjectives.has(objectiveId)) {
      // Already completed
      return false;
    }

    // Update current value
    if (increment) {
      objective.currentValue = (objective.currentValue || 0) + value;
    } else {
      objective.currentValue = value;
    }

    console.log(`📊 Progress update: ${objective.title} (${objective.currentValue}/${objective.targetValue || 1})`);

    // Trigger progress callback
    this.onObjectiveProgressCallbacks.forEach(cb => cb(objective));

    // Check if objective is now complete
    if (objective.targetValue !== undefined) {
      if (objective.currentValue >= objective.targetValue) {
        this.completeObjective(objectiveId);
      }
    }

    return true;
  }

  /**
   * Increment objective progress by 1
   */
  public incrementProgress(objectiveId: string): boolean {
    return this.updateProgress(objectiveId, 1, true);
  }

  /**
   * Mark objective as complete
   */
  public completeObjective(objectiveId: string, awardXP: boolean = true): boolean {
    const objective = this.objectives.get(objectiveId);
    if (!objective) {
      console.warn(`Objective not found: ${objectiveId}`);
      return false;
    }

    if (this.completedObjectives.has(objectiveId)) {
      console.warn(`Objective already completed: ${objectiveId}`);
      return false;
    }

    // Mark as completed
    this.completedObjectives.add(objectiveId);
    objective.completedAt = Date.now();

    // Set current value to target (for UI display)
    if (objective.targetValue !== undefined) {
      objective.currentValue = objective.targetValue;
    }

    console.log(`✅ Objective completed: ${objective.title}`);

    // Award XP if applicable
    if (awardXP && objective.rewardXP) {
      // Import UpgradeSystem to award XP
      import('./UpgradeSystem').then(({ upgradeSystem }) => {
        upgradeSystem.awardXP(objective.rewardXP!, `Objective: ${objective.title}`);
      });
    }

    // Trigger completion callback
    this.onObjectiveCompletedCallbacks.forEach(cb => cb(objective));

    // Check if all required objectives are complete
    if (this.areAllRequiredObjectivesComplete()) {
      console.log('🎉 All required objectives complete!');
      this.onAllObjectivesCompleteCallbacks.forEach(cb => cb());
    }

    return true;
  }

  /**
   * Get all objectives
   */
  public getObjectives(): ObjectiveDefinition[] {
    return Array.from(this.objectives.values());
  }

  /**
   * Get visible objectives (not hidden)
   */
  public getVisibleObjectives(): ObjectiveDefinition[] {
    return this.getObjectives().filter(obj => !obj.hidden);
  }

  /**
   * Get required objectives
   */
  public getRequiredObjectives(): ObjectiveDefinition[] {
    return this.getObjectives().filter(obj => obj.required);
  }

  /**
   * Get optional objectives
   */
  public getOptionalObjectives(): ObjectiveDefinition[] {
    return this.getObjectives().filter(obj => !obj.required);
  }

  /**
   * Get completed objectives
   */
  public getCompletedObjectives(): ObjectiveDefinition[] {
    return this.getObjectives().filter(obj => this.completedObjectives.has(obj.id));
  }

  /**
   * Get incomplete objectives
   */
  public getIncompleteObjectives(): ObjectiveDefinition[] {
    return this.getObjectives().filter(obj => !this.completedObjectives.has(obj.id));
  }

  /**
   * Get specific objective
   */
  public getObjective(objectiveId: string): ObjectiveDefinition | undefined {
    return this.objectives.get(objectiveId);
  }

  /**
   * Check if objective is completed
   */
  public isObjectiveComplete(objectiveId: string): boolean {
    return this.completedObjectives.has(objectiveId);
  }

  /**
   * Check if all required objectives are complete
   */
  public areAllRequiredObjectivesComplete(): boolean {
    const requiredObjectives = this.getRequiredObjectives();
    return requiredObjectives.every(obj => this.completedObjectives.has(obj.id));
  }

  /**
   * Get objective completion percentage
   */
  public getCompletionPercentage(requiredOnly: boolean = true): number {
    const objectives = requiredOnly ? this.getRequiredObjectives() : this.getObjectives();
    if (objectives.length === 0) return 100;

    const completed = objectives.filter(obj => this.completedObjectives.has(obj.id)).length;
    return (completed / objectives.length) * 100;
  }

  /**
   * Get objective progress (for objectives with target values)
   */
  public getObjectiveProgress(objectiveId: string): ObjectiveProgress | null {
    const objective = this.objectives.get(objectiveId);
    if (!objective) return null;

    return {
      objectiveId: objective.id,
      currentValue: objective.currentValue || 0,
      completed: this.completedObjectives.has(objectiveId),
    };
  }

  /**
   * Clear all objectives (for new night)
   */
  public clearObjectives(): void {
    this.objectives.clear();
    this.completedObjectives.clear();
    console.log('🗑️ All objectives cleared');
  }

  /**
   * Reset objective system (for new game)
   */
  public reset(): void {
    this.clearObjectives();
    this.onObjectiveCompletedCallbacks = [];
    this.onObjectiveRevealedCallbacks = [];
    this.onObjectiveProgressCallbacks = [];
    this.onAllObjectivesCompleteCallbacks = [];
    console.log('🔄 Objective system reset');
  }

  /**
   * Check objective conditions (for auto-completion)
   * Call this periodically to check if objectives should auto-complete
   */
  public checkObjectiveConditions(): void {
    const state = store.getState();

    this.getIncompleteObjectives().forEach(objective => {
      let shouldComplete = false;

      switch (objective.type) {
        case ObjectiveType.SURVIVE:
          // Check if required time has elapsed
          if (objective.requiredTime !== undefined) {
            if (state.game.timeElapsed >= objective.requiredTime) {
              shouldComplete = true;
            }
          }
          break;

        case ObjectiveType.COLLECT_ITEM:
          // Check inventory for required items
          if (objective.requiredItems && objective.requiredItems.length > 0) {
            import('./InventorySystem').then(({ inventorySystem }) => {
              const hasAllItems = objective.requiredItems!.every(itemId =>
                inventorySystem.hasItem(itemId)
              );
              if (hasAllItems) {
                this.completeObjective(objective.id);
              }
            });
          }
          break;

        case ObjectiveType.MAINTAIN_RESOURCE:
          // Check if resource is above threshold
          if (objective.targetValue !== undefined) {
            const battery = state.player.flashlightBattery;
            if (battery >= objective.targetValue) {
              shouldComplete = true;
            }
          }
          break;

        // Other objective types are manually completed via events
      }

      if (shouldComplete) {
        this.completeObjective(objective.id);
      }
    });
  }

  /**
   * Register callback for objective completion
   */
  public onObjectiveCompleted(callback: (objective: ObjectiveDefinition) => void): void {
    this.onObjectiveCompletedCallbacks.push(callback);
  }

  /**
   * Register callback for objective revealed
   */
  public onObjectiveRevealed(callback: (objective: ObjectiveDefinition) => void): void {
    this.onObjectiveRevealedCallbacks.push(callback);
  }

  /**
   * Register callback for objective progress
   */
  public onObjectiveProgress(callback: (objective: ObjectiveDefinition) => void): void {
    this.onObjectiveProgressCallbacks.push(callback);
  }

  /**
   * Register callback for all objectives complete
   */
  public onAllObjectivesComplete(callback: () => void): void {
    this.onAllObjectivesCompleteCallbacks.push(callback);
  }

  /**
   * Get objective statistics
   */
  public getStats(): {
    totalObjectives: number;
    requiredObjectives: number;
    optionalObjectives: number;
    completedObjectives: number;
    completionPercentage: number;
  } {
    const total = this.objectives.size;
    const required = this.getRequiredObjectives().length;
    const optional = this.getOptionalObjectives().length;
    const completed = this.completedObjectives.size;
    const percentage = this.getCompletionPercentage(false);

    return {
      totalObjectives: total,
      requiredObjectives: required,
      optionalObjectives: optional,
      completedObjectives: completed,
      completionPercentage: percentage,
    };
  }

  /**
   * Export objective data (for save system)
   */
  public exportObjectives(): {
    objectives: ObjectiveDefinition[];
    completedIds: string[];
  } {
    return {
      objectives: this.getObjectives(),
      completedIds: Array.from(this.completedObjectives),
    };
  }

  /**
   * Import objective data (for load system)
   */
  public importObjectives(data: { objectives: ObjectiveDefinition[]; completedIds: string[] }): void {
    this.clearObjectives();

    data.objectives.forEach(obj => {
      this.objectives.set(obj.id, obj);
    });

    data.completedIds.forEach(id => {
      this.completedObjectives.add(id);
    });

    console.log(`🎯 Imported ${data.objectives.length} objectives (${data.completedIds.length} completed)`);
  }

  /**
   * Create default objectives for a night (helper method for level designers)
   */
  public createNightObjectives(night: number): void {
    this.clearObjectives();

    // Common objectives for all nights
    this.addObjective({
      id: `night_${night}_survive`,
      type: ObjectiveType.SURVIVE,
      title: 'Survive until 6 AM',
      description: 'Make it through the night without being caught',
      required: true,
      hidden: false,
      requiredTime: 300, // 5 minutes (will be adjusted per night)
      rewardXP: 100,
    });

    this.addObjective({
      id: `night_${night}_return`,
      type: ObjectiveType.RETURN_TO_OFFICE,
      title: 'Return to Security Office',
      description: 'Get back to the security office before 6 AM',
      required: true,
      hidden: false,
      rewardXP: 50,
    });

    // Optional collectible objective
    this.addObjective({
      id: `night_${night}_collectibles`,
      type: ObjectiveType.FIND_COLLECTIBLE,
      title: 'Find Collectibles',
      description: 'Discover hidden lore items throughout the restaurant',
      required: false,
      hidden: false,
      targetValue: 3,
      currentValue: 0,
      rewardXP: 75,
    });

    console.log(`🎯 Created default objectives for Night ${night}`);
  }
}

// Export singleton instance
export const objectiveSystem = ObjectiveSystem.getInstance();
